/** Serverzustand des Tokenmanagements. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import {
  createProvider,
  deleteProvider,
  fetchProviders,
  testProvider,
  updateProvider,
  type ProviderPayload,
} from './api';

export const tokenKeys = {
  all: ['tokens'] as const,
  providers: ['tokens', 'providers'] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

export function useProviders() {
  return useQuery({
    queryKey: tokenKeys.providers,
    queryFn: fetchProviders,
    retry: retryUnlessPermanent,
  });
}

function useProviderMutation<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: tokenKeys.all });
      // Ein geaenderter Zugang aendert, was die Sicherungsuebersicht sieht.
      await queryClient.invalidateQueries({ queryKey: ['backup'] });
    },
  });
}

export function useCreateProvider() {
  return useProviderMutation((payload: ProviderPayload) => createProvider(payload));
}

export function useUpdateProvider() {
  return useProviderMutation((variables: { id: string; payload: Partial<ProviderPayload> }) =>
    updateProvider(variables.id, variables.payload),
  );
}

export function useDeleteProvider() {
  return useProviderMutation((providerId: string) => deleteProvider(providerId));
}

/**
 * Die Verbindungspruefung.
 *
 * Ohne Entwertung des Zwischenspeichers: Sie schreibt zwar den Zeitpunkt der
 * Pruefung in den Zugang, aber das Ergebnis steht in der Antwort und wird
 * neben dem Knopf angezeigt. Ein Neuladen der Liste ueberschriebe es mit dem
 * gespeicherten Stand -- und der sagt weniger.
 */
export function useTestProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (providerId: string) => testProvider(providerId),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: tokenKeys.providers });
    },
  });
}
